import { useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../../lib/api.js';
import { Page } from '../../app/AppShell.js';
import { useToast } from '../../components/Toast.js';
import { Loading, ErrorNote } from '../../components/ui.js';
import type { BoxBackupState, BoxBackupSummary } from '../../lib/types.js';

function size(bytes: number | null): string {
  if (bytes === null) return '?';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

function Contents({ backup }: { backup: BoxBackupSummary }): ReactNode {
  const c = backup.counts ?? {};
  return (
    <ul className="muted small" style={{ margin: '8px 0 12px', paddingLeft: 18 }}>
      {backup.placeName && <li>{backup.placeName}</li>}
      <li>
        {c.displays ?? 0} screens, {c.layouts ?? 0} layouts, {c.media ?? 0} media files ({size(backup.mediaBytes)})
      </li>
      {backup.createdAt && <li>Made {new Date(backup.createdAt).toLocaleString()}</li>}
      <li>{backup.caIncluded ? 'Includes the certificate, so screens keep trusting this box' : 'Does not include the certificate: devices will need to trust the box again'}</li>
    </ul>
  );
}

/** Akarya Box only: one file holds the whole box (database, media, certificate). */
export function BackupScreen(): ReactNode {
  const toast = useToast();
  const [state, setState] = useState<BoxBackupState | null>(null);
  const [error, setError] = useState<Error>();
  const [passphrase, setPassphrase] = useState('');
  const [restorePass, setRestorePass] = useState('');
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [askPass, setAskPass] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.boxBackupState().then(setState).catch((e: unknown) => setError(e instanceof Error ? e : new Error(String(e))));
  }, []);

  async function download(): Promise<void> {
    setBusy(true);
    try {
      const made = await api.createBoxBackup(passphrase);
      const link = document.createElement('a');
      link.href = api.boxBackupDownloadUrl(made.id);
      link.download = made.name;
      document.body.append(link);
      link.click();
      link.remove();
      toast.push(made.encrypted ? 'Backup downloaded (encrypted)' : 'Backup downloaded', 'success');
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  async function stage(file: File, pass: string): Promise<void> {
    setBusy(true);
    try {
      const res = await api.stageBoxRestore(file, pass);
      setState((s) => (s ? { ...s, staged: res.staged } : s));
      setPickedFile(null);
      setAskPass(false);
      setRestorePass('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && /passphrase/i.test(err.message)) {
        setPickedFile(file);
        setAskPass(true);
        if (pass) toast.error(err);
      } else {
        toast.error(err);
      }
    } finally {
      setBusy(false);
    }
  }

  async function discard(): Promise<void> {
    try {
      await api.discardBoxRestore();
      setState((s) => (s ? { ...s, staged: null } : s));
    } catch (err) {
      toast.error(err);
    }
  }

  if (error) return <Page title="Backup"><ErrorNote error={error} /></Page>;
  if (!state) return <Page title="Backup"><Loading what="backup" /></Page>;

  return (
    <Page title="Backup">
      <p className="muted small" style={{ marginBottom: 16 }}>
        One file holds everything on this box: screens, layouts, media, users and the certificate. Keep it somewhere safe,
        away from this PC.
      </p>

      <section className="card" style={{ padding: 16, marginBottom: 16 }}>
        <h3>Download backup</h3>
        {!state.caConfigured && (
          <p className="muted small">The box does not say where its certificate is kept, so this backup will not include it.</p>
        )}
        <details style={{ margin: '8px 0' }}>
          <summary className="muted small">Protect with a passphrase (optional)</summary>
          <input
            type="password"
            autoComplete="new-password"
            placeholder="At least 6 characters"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
          />
          <p className="muted small">You need it to restore. It cannot be recovered if lost.</p>
        </details>
        <button className="btn primary" disabled={busy || (passphrase !== '' && passphrase.length < 6)} onClick={() => void download()}>
          {busy ? 'Working…' : 'Download backup'}
        </button>
      </section>

      <section className="card" style={{ padding: 16 }}>
        <h3>Restore</h3>
        {state.staged ? (
          <>
            <p>This backup replaces everything on this box when the box restarts:</p>
            <Contents backup={state.staged} />
            <p className="muted small">Restart the box to apply it. Until then nothing has changed.</p>
            <button className="btn" onClick={() => void discard()}>
              Cancel restore
            </button>
          </>
        ) : (
          <>
            <p className="muted small">
              Choose a backup file. It is checked first and nothing changes until you restart the box.
            </p>
            <input
              type="file"
              accept=".akbox"
              disabled={busy}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void stage(file, '');
              }}
            />
            {askPass && pickedFile && (
              <form
                style={{ marginTop: 12 }}
                onSubmit={(e) => {
                  e.preventDefault();
                  void stage(pickedFile, restorePass);
                }}
              >
                <label>
                  Passphrase for {pickedFile.name}
                  <input type="password" autoFocus value={restorePass} onChange={(e) => setRestorePass(e.target.value)} />
                </label>
                <button className="btn primary" type="submit" disabled={busy || restorePass === ''}>
                  Check backup
                </button>
              </form>
            )}
          </>
        )}
      </section>
    </Page>
  );
}
